import React from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FormErrorMessage } from "go-form/components/FormErrorMessage";
import FormNumberInput from "go-form/components/FormNumberInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import {
	OrderPreparationStatusName,
	VenueSettingsApi,
} from "../../../../../../../../../../../services/Api/Organization/types";
import { VenueSettingsItemApi } from "../../../../../../../../../../../services/Api/types";
import { getOptionsByVenueSetting, getSettingCustomPlaceholder, isSettingANumberInput } from "../utils";
import {
	SettingsDriversType,
	SettingsOtherType,
	SettingsPreparationStatusesType,
	SettingsReportType,
} from "../utils/types";

interface Props {
	types: VenueSettingsItemApi[];
	form: UseFormReturn<VenueSettingsApi>;
	AdditionalSettings?: React.ReactNode;
}
const SettingsTable = ({ form, types, AdditionalSettings }: Props): JSX.Element => {
	const {
		formState: { errors },
		control,
		getValues,
		watch,
	} = form;
	const { t } = useTranslation();
	const {
		supported_preparation_statuses: { values: selectedSupportedPreparationStatuses },
		settings,
	} = watch();
	let saleStatusPreparationDefaultCurrentValue = "";
	const saleStatusPreparationDefaultConfig = settings.find(
		(setting: VenueSettingsItemApi) =>
			setting.type === SettingsPreparationStatusesType.SALE_STATUS_PREPERATION_DEFAULT
	);

	if (
		saleStatusPreparationDefaultConfig &&
		saleStatusPreparationDefaultConfig.value &&
		typeof saleStatusPreparationDefaultConfig.value !== "number"
	) {
		saleStatusPreparationDefaultCurrentValue = saleStatusPreparationDefaultConfig.value;
	}

	const getNumberInputDefaultValueTitle = (setting: VenueSettingsItemApi) => {
		let unit = "";
		switch (setting.type) {
			case SettingsDriversType.CITY_SEARCH_DISTANCE_KM:
				unit = "km";
				break;
			case SettingsDriversType.DRIVER_CAN_CHANGE_STATUS_TO_DELIVERED_FROM_METERS:
				unit = "m";
				break;
			case SettingsOtherType.USER_LOGOUT_THRESHOLD:
				unit = "s";
				break;
			case SettingsReportType.REPORT_POS_SETTLEMENT_ALERT_TIME:
				unit = "h";
				break;
			default:
				break;
		}

		if (unit) return `${setting.default_value} ${unit}`;
		return setting.default_value;
	};

	const getNumberInputMaxPermittedAmountOfDigits = (setting: VenueSettingsItemApi) => {
		switch (setting.type) {
			case SettingsReportType.REPORT_POS_SETTLEMENT_ALERT_TIME:
				return 0;
			default:
				return undefined;
		}
	};

	const getSelectGroupDefaultValue = (type: string, defaultValue: string) => {
		if (type === SettingsPreparationStatusesType.SALE_STATUS_PREPERATION_DEFAULT) {
			const saleStatusPreparationDefault = watch("settings").find(
				(setting) => setting.type === SettingsPreparationStatusesType.SALE_STATUS_PREPERATION_DEFAULT
			);

			if (saleStatusPreparationDefault?.value && saleStatusPreparationDefault.value !== "empty") {
				return {
					value: saleStatusPreparationDefault.value,
					label: t(`enums.preparation_statuses.${saleStatusPreparationDefault.value}`),
				};
			}
		}

		return defaultValue;
	};

	const shouldForceClearInput = (type: string) => {
		const defaultPreparationStatusIsSupported = selectedSupportedPreparationStatuses.includes(
			saleStatusPreparationDefaultCurrentValue as OrderPreparationStatusName
		);

		if (type !== SettingsPreparationStatusesType.SALE_STATUS_PREPERATION_DEFAULT) return false;

		if (
			saleStatusPreparationDefaultCurrentValue === "empty" &&
			!selectedSupportedPreparationStatuses.includes(OrderPreparationStatusName.NEW)
		)
			return true;

		return (
			!defaultPreparationStatusIsSupported &&
			selectedSupportedPreparationStatuses.length !== 0 &&
			saleStatusPreparationDefaultCurrentValue !== "empty"
		);
	};

	return (
		<>
			<thead>
				<tr>
					<th>{t("common.word.name", { ns: "lib" })}</th>
					<th>{t("modules.venue_settings.field.default.title")}</th>
					<th className={"w-20"}>{t("common.word.value")}</th>
				</tr>
			</thead>
			<tbody>
				{types.map((type) => {
					const index = getValues().settings.findIndex((value) => value.type === type.type);
					const defaultValue = getValues().settings[index]?.default_value ?? "true";
					const isNumberInput = isSettingANumberInput(type);
					const customPlaceholder = getSettingCustomPlaceholder(type, t);
					const forceClear = shouldForceClearInput(type.type);
					const description = type.link ? (
						<div>
							<span className="text-muted">{type.link.description}</span>
							<Link to={type.link.to} target="_blank">
								{type.link.name}
							</Link>
						</div>
					) : (
						<span className={"text-muted"}>{t(`enums.venue_settings.${type.type}.desc`)}</span>
					);

					return (
						<tr key={type.type}>
							<td className={"w-70"}>
								<div className={"d-flex flex-column"}>
									<span>{t(`enums.venue_settings.${type.type}.label`)}</span>
									{description}
								</div>
							</td>
							<td>
								<span className={"venue-default-setting"}>
									{isNumberInput
										? getNumberInputDefaultValueTitle(type)
										: t(`modules.venue_settings.field.${type.default_value}.title`)}
								</span>
							</td>
							<td>
								{isNumberInput ? (
									<>
										<FormNumberInput
											errors={errors}
											control={control}
											name={`settings.${index}.value`}
											allowNegative={false}
											suffix=""
											defaultValue={defaultValue}
											maxPermittedAmountOfDigits={getNumberInputMaxPermittedAmountOfDigits(type)}
										/>
										<FormErrorMessage errors={errors} name={`settings.${type.type}`} />
									</>
								) : (
									<>
										<FormSelectGroup
											control={control}
											errors={errors}
											name={`settings.${index}.value`}
											defaultValue={getSelectGroupDefaultValue(type.type, defaultValue)}
											options={getOptionsByVenueSetting(
												defaultValue,
												type.type,
												t,
												selectedSupportedPreparationStatuses
											)}
											placeholder={customPlaceholder}
											data-testid={`settings.${index}.value`}
											forceClear={forceClear}
										/>
										<FormErrorMessage errors={errors} name={`settings.${type.type}`} />
									</>
								)}
							</td>
						</tr>
					);
				})}
				{AdditionalSettings}
			</tbody>
		</>
	);
};
export default SettingsTable;

// t("modules.venue_settings.field.0.title");
// t("modules.venue_settings.field.1.title");
// t("modules.venue_settings.field.empty.title");
// t("modules.venue_settings.field.no.title");
// t(`modules.venue_settings.field.true.title`);
// t(`modules.venue_settings.field.false.title`);
// t(`modules.venue_settings.field.ShiftWork.title`);
